/**
 * @fileoverview Shared live progress indicator for import dry-run and commit.
 * @module import-editor/components/ImportProgressIndicator
 *
 * Responsibilities:
 * - Render live import progress from a backend-written summary snapshot.
 * - Derive processed rows, percentage, elapsed time, rate, and ETA.
 * - Tolerate partial failed-run summaries while local-first sync catches up.
 *
 * Dependencies:
 * - React state/effects.
 * - lucide-react status icons.
 *
 * Architecture:
 * - Presentation-only shared component; callers provide Dexie-backed run summary state.
 * - No API calls, persistence, or synchronization side effects are performed here.
 *
 * Notes:
 * - Missing nested summary buckets are treated as zero to avoid crashing on failed runs.
 */

import { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, XCircle, Clock, Gauge } from 'lucide-react';

/**
 * @contract ProgressPhase
 *
 * Purpose:
 * - Enumerate import lifecycle phases displayed by the progress indicator.
 *
 * Fields:
 * - queued/dry-run/commit/finalizing/done/failed/cancelled: supported UI states.
 *
 * Rules:
 * - Values must align with caller-local import run state.
 *
 * Sync:
 * - Derived from local-first ImportRun projections or active operation state.
 */
export type ProgressPhase = 'queued' | 'dry-run' | 'commit' | 'finalizing' | 'done' | 'failed' | 'cancelled';

/**
 * @contract ProgressStage
 *
 * Purpose:
 * - Fine-grained step the backend processor reports during a run, so the header
 *   shows what is actually happening during the long phases that emit no row
 *   counters (downloading the source file, counting rows, pre-loading existing
 *   records, reconciling removed records, geocoding) — which previously made the
 *   wizard look frozen.
 *
 * Fields:
 * - Mirrors ImportProgressStage in @firesquirrel/import-core. Advisory only;
 *   `phase` remains the lifecycle source of truth.
 *
 * Sync:
 * - Read from the ImportRun document's progressStage field via the wizard adapter.
 */
export type ProgressStage =
  | 'preparing'
  | 'downloading'
  | 'reading'
  | 'counting'
  | 'loadingExisting'
  | 'analyzing'
  | 'writing'
  | 'reconciling'
  | 'geocoding'
  | 'fireHazardStamping'
  | 'finalizing'
  | 'completed';

/**
 * @contract ProgressSummary
 *
 * Purpose:
 * - Represent live import summary counters projected into the run UI.
 *
 * Fields:
 * - totalRows/created/updated/softDeleted: high-level import counters.
 * - excluded: row exclusion buckets.
 * - failedByKind: row failure buckets.
 *
 * Rules:
 * - Runtime records may be partial while a run is failed, active, or syncing.
 *
 * Sync:
 * - Supplied by callers from local-first ImportRun projections.
 */
export interface ProgressSummary {
  totalRows: number;
  created: number;
  updated: number;
  softDeleted?: number;
  excluded: {
    outOfJurisdiction: number;
    missingCoordinates: number;
    duplicateInsertOnly: number;
    missingMatchKey: number;
    /** parcelOwnerUpdate only: no entity matched (update-only). */
    noMatch?: number;
    /** parcelOwnerUpdate only: existing ownership.sourceAsOf is newer. */
    staleSource?: number;
  };
  failedByKind: {
    transform: number;
    entityBuild: number;
  };
  skippedByUser?: number;
  /** Phase 5.6: Enrichment-specific counters. Only present for enrichment runs. */
  enrichment?: {
    candidates: number;
    enriched: number;
    alreadyComplete: number;
    noData: number;
  };
}

/**
 * @contract ImportProgressIndicatorProps
 *
 * Purpose:
 * - Configure the shared progress indicator.
 *
 * Fields:
 * - phase: current run phase.
 * - summary: local-first summary snapshot or null before counters exist.
 * - startedAt: ISO timestamp used for elapsed/rate calculations.
 * - frozen: stops timer updates after terminal states.
 * - errorMessage: terminal failure message.
 *
 * Rules:
 * - Component is presentation-only and must not fetch or mutate run state.
 *
 * Sync:
 * - Parent components own summary refresh through local-first services.
 */
export interface ImportProgressIndicatorProps {
  phase: ProgressPhase;
  /** Live snapshot of the run's summary (from poll) */
  summary: ProgressSummary | null;
  /** ISO timestamp when the run was created (for elapsed time). Defaults to now. */
  startedAt?: string;
  /** Optional: set to true after the final terminal state to freeze the timer */
  frozen?: boolean;
  /** Optional error message for phase='failed' */
  errorMessage?: string | null;
  /** Fine-grained step from the backend (progressStage). When present and the run
      is active, drives the header label so long no-counter phases stay visible. */
  stage?: ProgressStage | null;
  /** Human-readable detail for the current stage (progressDetail). Preferred over
      the generic phase label while the run is active. */
  stageDetail?: string | null;
}

function formatDuration(ms: number): string {
  if (ms < 0 || !isFinite(ms)) return '—';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatRate(rps: number): string {
  if (!isFinite(rps) || rps <= 0) return '—';
  if (rps >= 1000) return `${(rps / 1000).toFixed(1)}k rows/s`;
  if (rps >= 10) return `${Math.round(rps)} rows/s`;
  return `${rps.toFixed(1)} rows/s`;
}

function phaseLabel(phase: ProgressPhase): string {
  switch (phase) {
    case 'queued':     return 'Queued — waiting for worker';
    case 'dry-run':    return 'Analyzing your data';
    case 'commit':     return 'Writing records to the database';
    case 'finalizing': return 'Finalizing';
    case 'done':       return 'Complete';
    case 'failed':     return 'Failed';
    case 'cancelled':  return 'Cancelled';
  }
}

/**
 * Fallback label for a fine-grained stage when the backend did not supply a
 * progressDetail string (older runs). Returns null for unknown/terminal stages
 * so the caller falls back to the generic phase label.
 */
function stageLabel(stage: ProgressStage | null | undefined): string | null {
  switch (stage) {
    case 'preparing':       return 'Preparing import…';
    case 'downloading':     return 'Downloading source file…';
    case 'reading':         return 'Reading and validating the file…';
    case 'counting':        return 'Counting rows in the source file…';
    case 'loadingExisting': return 'Loading existing records…';
    case 'analyzing':       return 'Analyzing your data…';
    case 'writing':         return 'Writing records to the database…';
    case 'reconciling':     return 'Reconciling records removed from the source file…';
    case 'geocoding':           return 'Filling in missing coordinates…';
    case 'fireHazardStamping':  return 'Stamping parcels with fire hazard levels…';
    case 'finalizing':          return 'Finalizing and generating the import report…';
    default:                return null;
  }
}

/**
 * @component ImportProgressIndicator
 *
 * Purpose:
 * - Display live row-processing progress for import dry-run and commit operations.
 *
 * Props:
 * - ImportProgressIndicatorProps.
 *
 * Behavior:
 * - Computes processed rows, percent, rate, ETA, and terminal error display from summary input.
 *
 * Data Source:
 * - local-first caller-provided summary state.
 *
 * Constraints:
 * - Presentation-only; does not call APIs or write persistence.
 *
 * Performance:
 * - Uses a 500ms timer only while not frozen.
 */
export function ImportProgressIndicator({
  phase,
  summary,
  startedAt,
  frozen = false,
  errorMessage,
  stage,
  stageDetail,
}: ImportProgressIndicatorProps) {
  // Ticking clock — re-render every 500ms so elapsed/ETA feel alive.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (frozen) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [frozen]);

  const totalRows     = summary?.totalRows    ?? 0;
  const created       = summary?.created      ?? 0;
  const updated       = summary?.updated      ?? 0;
  const softDeleted   = summary?.softDeleted  ?? 0;
  const outOfJurisdiction   = summary?.excluded?.outOfJurisdiction   ?? 0;
  const missingCoordinates  = summary?.excluded?.missingCoordinates  ?? 0;
  const duplicateInsertOnly = summary?.excluded?.duplicateInsertOnly ?? 0;
  const missingMatchKey     = summary?.excluded?.missingMatchKey     ?? 0;
  const transformFailed     = summary?.failedByKind?.transform       ?? 0;
  const entityBuildFailed   = summary?.failedByKind?.entityBuild     ?? 0;
  const skippedByUser       = summary?.skippedByUser                 ?? 0;
  const noMatch           = summary?.excluded?.noMatch           ?? 0;
  const staleSource       = summary?.excluded?.staleSource       ?? 0;
  const excludedTotal = outOfJurisdiction + missingCoordinates + duplicateInsertOnly + missingMatchKey + noMatch + staleSource;
  const failedTotal   = transformFailed + entityBuildFailed;
  const rowsProcessed = created + updated + excludedTotal + failedTotal + skippedByUser;

  const startMs       = startedAt ? new Date(startedAt).getTime() : now;
  const elapsedMs     = Math.max(0, now - startMs);
  const rate          = rowsProcessed > 0 && elapsedMs > 0 ? (rowsProcessed / (elapsedMs / 1000)) : 0;
  const remaining     = Math.max(0, totalRows - rowsProcessed);
  const etaMs         = rate > 0 && remaining > 0 ? (remaining / rate) * 1000 : null;
  const percent       = totalRows > 0 ? Math.min(100, (rowsProcessed / totalRows) * 100) : 0;

  const isActive   = phase === 'queued' || phase === 'dry-run' || phase === 'commit' || phase === 'finalizing';
  const isTerminal = phase === 'done' || phase === 'failed' || phase === 'cancelled';

  // While the run is active, prefer the fine-grained backend stage so the user
  // sees the actual step (downloading, counting, reconciling…) instead of a
  // generic "Writing records" that sits unchanged through the long no-counter
  // phases. Falls back to the canned stage label, then the phase label.
  const headerLabel = isActive
    ? (stageDetail || stageLabel(stage) || phaseLabel(phase))
    : phaseLabel(phase);

  // Color scheme switches on phase so the bar reads differently for dry-run
  // (blue — informational) vs commit (green — destructive write) vs failed.
  const barColor =
    phase === 'failed' || phase === 'cancelled' ? 'bg-red-500'
    : phase === 'done'                           ? 'bg-emerald-500'
    : phase === 'commit' || phase === 'finalizing' ? 'bg-emerald-500'
    : 'bg-blue-500';

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Header: phase + elapsed */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          {phase === 'failed' || phase === 'cancelled'
            ? <XCircle className="h-4 w-4 text-red-500" />
            : phase === 'done'
              ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              : <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />}
          <span className="text-sm font-semibold text-gray-800">{headerLabel}</span>
          {phase === 'queued' && (
            <span className="text-[11px] text-gray-500 italic">— about to start…</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {formatDuration(elapsedMs)}
          </span>
          {isActive && phase !== 'finalizing' && rate > 0 && (
            <span className="inline-flex items-center gap-1" title="Processing rate (rows per second)">
              <Gauge className="h-3 w-3" /> {formatRate(rate)}
            </span>
          )}
          {isActive && phase !== 'finalizing' && etaMs !== null && (
            <span title="Estimated time remaining">~ {formatDuration(etaMs)} left</span>
          )}
        </div>
      </div>

      {/* Main progress bar + headline count */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-baseline justify-between mb-1.5">
          <div className="text-base font-semibold text-gray-900">
            {rowsProcessed.toLocaleString()}
            <span className="text-gray-400 font-normal"> of </span>
            {totalRows > 0 ? totalRows.toLocaleString() : '—'}
            <span className="text-gray-500 font-normal text-sm"> rows</span>
          </div>
          <div className="text-sm font-semibold text-gray-700 tabular-nums">
            {totalRows > 0 ? `${percent.toFixed(1)}%` : ''}
          </div>
        </div>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all duration-300 ease-out ${
              // Pulse ONLY during the brief pre-count window where totalRows is
              // still unknown. Once countRows completes the bar becomes a real
              // determinate 0%→100% bar — no animation, no jumpy width swings.
              isActive && totalRows === 0 ? 'animate-pulse' : ''
            }`}
            style={{ width: totalRows > 0 ? `${percent}%` : (isActive ? '8%' : '0%') }}
          />
        </div>
      </div>

      {/* Counter pills — updated live */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 px-4 pb-3">
        <CounterPill label="Created"          value={created}          color="emerald" />
        <CounterPill label="Updated"          value={updated}          color="blue" />
        <CounterPill label="Excluded"         value={excludedTotal}    color="amber" />
        <CounterPill label="Errors"           value={failedTotal}      color="red" />
        <CounterPill label="Out of bounds"    value={outOfJurisdiction} color="purple" hidden={outOfJurisdiction === 0 && !isTerminal} />
        {softDeleted > 0 && (
          <CounterPill label="Soft-deleted" value={softDeleted} color="orange" />
        )}
        {skippedByUser > 0 && (
          <CounterPill label="Skipped" value={skippedByUser} color="gray" />
        )}
        {summary?.enrichment && (
          <>
            <CounterPill
              label="Enriched"
              value={summary.enrichment.enriched}
              color="emerald"
            />
            <CounterPill
              label="Already complete"
              value={summary.enrichment.alreadyComplete}
              color="gray"
              hidden={summary.enrichment.alreadyComplete === 0 && !isTerminal}
            />
            <CounterPill
              label="No data"
              value={summary.enrichment.noData}
              color="amber"
              hidden={summary.enrichment.noData === 0 && !isTerminal}
            />
          </>
        )}
      </div>

      {/* Failure banner */}
      {phase === 'failed' && errorMessage && (
        <div className="px-4 py-2 border-t border-red-100 bg-red-50 text-xs text-red-700">
          {errorMessage}
        </div>
      )}
    </div>
  );
}

interface CounterPillProps {
  label: string;
  value: number;
  color: 'emerald' | 'blue' | 'amber' | 'red' | 'purple' | 'orange' | 'gray';
  hidden?: boolean;
}

function CounterPill({ label, value, color, hidden }: CounterPillProps) {
  if (hidden) return null;
  // Tailwind can't JIT these from dynamic strings — keep full class literals.
  const scheme: Record<CounterPillProps['color'], string> = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue:    'bg-blue-50 border-blue-200 text-blue-800',
    amber:   'bg-amber-50 border-amber-200 text-amber-800',
    red:     'bg-red-50 border-red-200 text-red-800',
    purple:  'bg-purple-50 border-purple-200 text-purple-800',
    orange:  'bg-orange-50 border-orange-200 text-orange-800',
    gray:    'bg-gray-50 border-gray-200 text-gray-800',
  };
  return (
    <div className={`border rounded-md px-2 py-1.5 text-center ${scheme[color]}`}>
      <div className="text-base font-bold tabular-nums leading-tight">{value.toLocaleString()}</div>
      <div className="text-[10px] font-medium uppercase tracking-wide opacity-80 leading-tight">{label}</div>
    </div>
  );
}
